import { useTranslation } from 'react-i18next';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { RelativeTime } from '../../../../components/design-system/admin';
import { Space } from '../../../vendor/my-spaces/models/space.model';
import { SpaceActionsMenu } from './space-actions-menu';
import { SurfaceList } from './surface-list';
import { DemoRoomBadge } from './demo-room-badge';

export interface SpacesViewProps {
    /** The spaces of the current page. */
    spaces: Space[];
    /** Opens the space's details (SpaceView). */
    onView: (space: Space) => void;
    onDelete: (space: Space) => void;
    onOpenInManager?: (space: Space) => void;
    onToggleDemo?: (space: Space) => void;
}

/** The library as a table: photo and name (opens the details), room, size, surfaces, when it was added. */
export const SpacesAsList = ({ spaces, onView, onDelete, onOpenInManager, onToggleDemo }: SpacesViewProps) => {
    const { t } = useTranslation();

    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead className="ps-4">{t('ADMIN_SPACES.COL.SPACE')}</TableHead>
                        <TableHead>{t('ADMIN_SPACES.COL.ROOM_TYPE')}</TableHead>
                        <TableHead className="text-end">{t('ADMIN_SPACES.COL.SIZE')}</TableHead>
                        <TableHead>{t('ADMIN_SPACES.COL.SURFACES')}</TableHead>
                        <TableHead>{t('ADMIN_SPACES.COL.ADDED')}</TableHead>
                        <TableHead className="w-12 pe-4">
                            <span className="sr-only">{t('ADMIN_SHELL.COMMON.ACTIONS')}</span>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {spaces.map((space, index) => {
                        const name = space.name || t('ADMIN_SPACES.UNNAMED');
                        return (
                            <TableRow key={space.id || index} data-testid="space-row">
                                <TableCell className="ps-4">
                                    <button
                                        type="button"
                                        onClick={() => onView(space)}
                                        className="group flex min-w-0 items-center gap-3 text-start focus-visible:outline-none"
                                        aria-label={t('ADMIN_SPACES.VIEW_DETAILS', { name })}
                                    >
                                        <span className="block size-12 shrink-0 overflow-hidden rounded-md bg-muted ring-1 ring-foreground/10">
                                            {space.thumbnailPath && (
                                                <img src={space.thumbnailPath} alt="" loading="lazy" className="size-full object-cover" />
                                            )}
                                        </span>
                                        <span className="truncate text-sm font-medium group-hover:underline group-focus-visible:underline">
                                            {name}
                                        </span>
                                        {space.isDemoDefault && <DemoRoomBadge />}
                                    </button>
                                </TableCell>
                                <TableCell className="text-sm">
                                    {space.spaceType || <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell className="text-end text-sm tabular-nums">
                                    {space.height || space.width
                                        ? t('ADMIN_SPACES.SIZE_CM', { height: space.height, width: space.width })
                                        : <span className="text-muted-foreground">—</span>}
                                </TableCell>
                                <TableCell>
                                    <SurfaceList surfaces={space.surfaces} />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {space.createdAt ? <RelativeTime value={space.createdAt} /> : '—'}
                                </TableCell>
                                <TableCell className="pe-4 text-end">
                                    <SpaceActionsMenu space={space} name={name} onOpenInManager={onOpenInManager} onToggleDemo={onToggleDemo} onDelete={onDelete} />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};
